import type { CustomPropertyDef } from '../types/layer.js';

/** Default object name parameter key for new layers. */
export const DEFAULT_FEATURE_NAME_PROPERTY = 'Название';

/** Default layer parameter for object display names (created with the first feature). */
export const DEFAULT_FEATURE_NAME_CUSTOM_PROPERTY: CustomPropertyDef = {
  name: DEFAULT_FEATURE_NAME_PROPERTY,
  type: 'title',
};

/**
 * A title-type parameter that actually names a property key. The schema lives in free-form
 * jsonb, so a malformed entry (`null`, no name, an empty name) must neither pick the name key
 * nor break the lookup.
 */
export function isNamedTitleProperty(def: unknown): def is { name: string; type: 'title' } {
  if (!def || typeof def !== 'object') {
    return false;
  }
  const { name, type } = def as { name?: unknown; type?: unknown };
  return type === 'title' && typeof name === 'string' && name.trim() !== '';
}

/**
 * Resolves which property key stores the display name for a layer.
 * Prefers an explicit title-type parameter; falls back to 'Название'. Malformed schema
 * entries are skipped (see `isNamedTitleProperty`).
 */
export function resolveFeatureNameKey(customProperties: unknown): string {
  if (!Array.isArray(customProperties)) {
    return DEFAULT_FEATURE_NAME_PROPERTY;
  }
  return (
    (customProperties as unknown[]).find(isNamedTitleProperty)?.name ??
    DEFAULT_FEATURE_NAME_PROPERTY
  );
}

/**
 * Keys `readFeatureDisplayName` reads the display name from, in priority order. Integration
 * sync promotes the first of them found in the source to the layer's title parameter.
 */
export const DISPLAY_NAME_KEYS = [DEFAULT_FEATURE_NAME_PROPERTY, 'name', 'title', 'label', 'Name', 'TITLE'] as const;

/**
 * The key that currently supplies the display name (see `readFeatureDisplayName`);
 * `undefined` — none of them holds a non-empty string. Renaming writes there, so the name
 * shown in lists changes instead of landing in a field nobody reads.
 */
export function resolveFeatureDisplayNameKey(
  properties: Record<string, unknown>,
): string | undefined {
  return DISPLAY_NAME_KEYS.find((key) => {
    const value = properties[key];
    return typeof value === 'string' && value.trim().length > 0;
  });
}

/**
 * Reads the display name from feature properties, checking 'Название', legacy 'name',
 * and other common import keys.
 */
export function readFeatureDisplayName(
  properties: Record<string, unknown>,
): string | undefined {
  const key = resolveFeatureDisplayNameKey(properties);
  return key === undefined ? undefined : (properties[key] as string).trim();
}
